/**
 * Factory for simple React setState + persist + optional dataChange trigger.
 * Persist / triggers run OUTSIDE the setState updater (Strict Mode safe).
 */
export function createSyncFieldSetter({
  setState,
  field,
  enqueueBusinessFieldSave,
  scheduleDataChangeTrigger,
  beforePersist,
  afterComputeNext,
  shouldPersist
}) {
  return (nextOrUpdater) => {
    let prevSnap;
    let nextSnap;
    let changed = false;
    let doPersist = false;

    setState((prev) => {
      const nextRaw =
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      const next = afterComputeNext ? afterComputeNext(nextRaw, prev) : nextRaw;

      if (next === prev) {
        return prev;
      }

      prevSnap = prev;
      nextSnap = next;
      changed = true;
      doPersist = shouldPersist ? shouldPersist(next, prev) : true;
      return next;
    });

    // Concrete value + updater never ran (timing / unmounted) → still push persist.
    if (!changed && typeof nextOrUpdater !== 'function') {
      const fallback = afterComputeNext
        ? afterComputeNext(nextOrUpdater, undefined)
        : nextOrUpdater;
      if (fallback !== undefined) {
        nextSnap = fallback;
        changed = true;
        doPersist = true;
        try {
          setState(fallback);
        } catch {
          /* ignore */
        }
      }
    }

    // Side effects outside updater — React 18 Strict Mode may double-invoke updaters.
    if (!changed) return;

    if (doPersist) {
      if (beforePersist) {
        beforePersist(nextSnap, prevSnap);
      } else if (typeof enqueueBusinessFieldSave === 'function') {
        enqueueBusinessFieldSave(field, nextSnap);
      }
    }

    if (scheduleDataChangeTrigger) {
      scheduleDataChangeTrigger(field);
    }
  };
}
